import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Card, Tag, Typography, Select, Button, Tooltip, theme, Input, DatePicker, Space, Row, Col, Popconfirm, Popover, message } from '../../ui';
import { DollarSign, Wallet, Clock, CheckCircle2, XCircle, Link, Pencil, Trash2, Eye } from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';
import { disableFuture } from '../../lib/dateRange';
import { request } from '../../api/client';
import { usePermission } from '../../store/auth';
import { TwDataTable as DataTable, DataTableColumn } from '../../components/TwDataTable';
import { useDebounce } from '../../hooks/useDebounce';
import { formatIstDate } from '../../lib/time';
import { PaymentFormModal } from './PaymentFormModal';
import { EditPaymentModal, EditablePayment } from './EditPaymentModal';
import { PaymentReviewModal } from './PaymentReviewModal';

const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const inr2 = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const APPROVAL: Record<string, { color: string; label: string }> = {
  PENDING: { color: 'gold', label: 'Pending approval' },
  APPROVED: { color: 'green', label: 'Approved' },
  REJECTED: { color: 'red', label: 'Rejected' },
};
const MODES = ['CASH', 'CHEQUE', 'UPI', 'NEFT', 'RTGS', 'IMPS', 'CARD', 'OTHER'];

/**
 * Payments are order-based now (invoice allocation retired). Retailer/SP/SH submissions arrive
 * PENDING; Accounts/Admin/SA approve (posts the ledger credit) or reject. Staff can also record a
 * payment directly (auto-approved) via "Record Payment".
 */
export function PaymentsListPage() {
  const { token } = theme.useToken();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const canRecord = usePermission('PAYMENT.CREATE');
  const canApprove = usePermission('PAYMENT.APPROVE');
  const canEditP = usePermission('PAYMENT.EDIT');
  const canDeleteP = usePermission('PAYMENT.DELETE');
  const canViewRetailers = usePermission('RETAILER.VIEW');

  const [status, setStatus] = useState<string | undefined>();
  const [mode, setMode] = useState<string | undefined>();
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 400);
  const [retailerId, setRetailerId] = useState<string | undefined>();
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [recordOpen, setRecordOpen] = useState(false);
  const [editFor, setEditFor] = useState<EditablePayment | null>(null);
  const [reviewFor, setReviewFor] = useState<any | null>(null);

  const params = useMemo(() => {
    const p: Record<string, any> = {};
    if (status) p.status = status;
    if (mode) p.mode = mode;
    if (search.trim()) p.search = search.trim();
    if (retailerId) p.retailerId = retailerId;
    if (range?.[0] && range?.[1]) { p.from = range[0].format('YYYY-MM-DD'); p.to = range[1].format('YYYY-MM-DD'); }
    return p;
  }, [status, mode, search, retailerId, range]);

  const { data, isLoading } = useQuery({
    queryKey: ['payments', params],
    queryFn: () => request<any[]>({ url: '/payments', params }),
    placeholderData: keepPreviousData,
  });
  const rows = data ?? [];

  const { data: retailers } = useQuery({
    queryKey: ['payment-retailer-filter'],
    queryFn: () => request<any>({ url: '/retailers', params: { pageSize: 1000 } }),
    enabled: canViewRetailers,
  });

  const kpis = useMemo(() => {
    let pendingAmt = 0, pendingCnt = 0, approvedAmt = 0, rejectedCnt = 0;
    for (const p of rows) {
      const a = Number(p.amount || 0);
      if (p.approvalStatus === 'PENDING') { pendingAmt += a; pendingCnt++; }
      // "Approved" is a CASH figure — an applied-credit row settled an order but no money came in, so exclude it.
      else if (p.approvalStatus === 'APPROVED' && !p.fromCredit) approvedAmt += a;
      else if (p.approvalStatus === 'REJECTED') rejectedCnt++;
    }
    return { count: rows.length, pendingAmt, pendingCnt, approvedAmt, rejectedCnt };
  }, [rows]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['payments'] });
    qc.invalidateQueries({ queryKey: ['orders'] });
    qc.invalidateQueries({ queryKey: ['retailer-ledger'] });
    qc.invalidateQueries({ queryKey: ['badges'] });
  };

  const del = useMutation({
    mutationFn: (id: string) => request({ url: `/payments/${id}`, method: 'DELETE' }),
    onSuccess: () => { message.success('Payment deleted'); invalidate(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to delete'),
  });

  const proofCell = (p: any) => {
    const files: string[] = (p.attachmentUrls?.length ? p.attachmentUrls : (p.attachmentUrl ? [p.attachmentUrl] : []));
    if (!files.length) return <span style={{ color: token.colorTextTertiary }}>—</span>;
    if (files.length === 1) {
      return <Tooltip label="Open proof"><a href={files[0]} target="_blank" rel="noopener" aria-label="Open proof" onClick={(e) => e.stopPropagation()} className="inline-flex h-8 w-8 items-center justify-center rounded-control text-content hover:bg-surface-2"><Link size={16} /></a></Tooltip>;
    }
    return (
      <Popover title="Proof files" trigger="click"
        content={<Space direction="vertical" size={2} onClick={(e) => e.stopPropagation()}>{files.map((u, i) => <a key={i} href={u} target="_blank" rel="noopener">Proof {i + 1}</a>)}</Space>}>
        <Button variant="text" size="sm" icon={<Link size={16} />} onClick={(e) => e.stopPropagation()}>{files.length}</Button>
      </Popover>
    );
  };

  const columns: DataTableColumn<any>[] = [
    { key: 'paymentNo', title: 'Payment No', dataIndex: 'paymentNo', sortable: true, alwaysVisible: true },
    { key: 'paymentDate', title: 'Date', dataIndex: 'paymentDate', sortable: true, render: (v: any) => formatIstDate(v) },
    {
      key: 'retailer', title: 'Retailer', alwaysVisible: true,
      render: (_: any, p: any) => (p.retailerName
        ? <div><div style={{ fontWeight: 600 }}>{p.retailerName}</div>{p.retailerCode && <div style={{ fontSize: 12, color: token.colorTextTertiary }}>{p.retailerCode}</div>}</div>
        : <span style={{ color: token.colorTextTertiary }}>#{p.retailerId}</span>),
    },
    {
      key: 'orderNo', title: 'Order', dataIndex: 'orderNo',
      render: (v: any, p: any) => v
        ? <Tooltip label="Open order"><Tag tone="blue" style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); navigate(`/orders/${p.orderId}`); }}>{v}</Tag></Tooltip>
        : <span style={{ color: token.colorTextTertiary }}>On-account</span>,
    },
    { key: 'mode', title: 'Mode', dataIndex: 'mode', width: 100, render: (v: string, p: any) => p.fromCredit ? <Tag tone="green">Credit</Tag> : <Tooltip label={p.referenceNo || p.utr || ''}><Tag>{v}</Tag></Tooltip> },
    { key: 'amount', title: 'Amount', dataIndex: 'amount', align: 'right', sortable: true, render: (v: any) => <b>{inr2(v)}</b> },
    {
      key: 'approvalStatus', title: 'Status', dataIndex: 'approvalStatus', sortable: true,
      render: (s: string, p: any) => {
        const m = APPROVAL[s] ?? { color: 'neutral', label: s };
        const tag = <Tag tone={m.color as any}>{m.label}</Tag>;
        return s === 'REJECTED' && p.rejectedReason ? <Tooltip label={p.rejectedReason}>{tag}</Tooltip> : tag;
      },
    },
    {
      key: 'submittedBy', title: 'Submitted by',
      render: (_: any, p: any) => {
        const name = p.submittedByName || p.recordedByName;
        if (!name) return '—';
        return (
          <Space size={6}>
            {p.submittedByRole === 'Retailer'
              ? <a onClick={(e) => { e.stopPropagation(); navigate(`/retailers/${p.retailerId}`); }}>{name}</a>
              : <span>{name}</span>}
            {p.submittedByRole && <Tag tone={p.submittedByRole === 'Retailer' ? 'green' : 'blue'} style={{ margin: 0 }}>{p.submittedByRole}</Tag>}
          </Space>
        );
      },
    },
    { key: 'proof', title: 'Proof', width: 70, align: 'center', render: (_: any, p: any) => proofCell(p) },
    {
      key: 'actions', title: '', align: 'right', width: 240, alwaysVisible: true,
      render: (_: any, p: any) => (
        <Space size={4} onClick={(e) => e.stopPropagation()}>
          {p.approvalStatus === 'PENDING' && canApprove && (
            <Button size="sm" variant="primary" icon={<Eye size={16} />} onClick={() => setReviewFor(p)}>Review</Button>
          )}
          {p.approvalStatus === 'APPROVED' && canEditP && (
            <Tooltip label="Edit payment"><Button variant="text" size="sm" icon={<Pencil size={16} />} onClick={() => setEditFor(p)} aria-label="Edit payment" /></Tooltip>
          )}
          {canDeleteP && p.approvalStatus !== 'REJECTED' && (
            <Popconfirm
              title="Delete this payment?"
              description={p.approvalStatus === 'APPROVED' ? 'Hard delete — reverses the ledger credit. Cannot be undone.' : 'Removes this pending submission.'}
              okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => del.mutate(p.id)}>
              <Button size="sm" variant="text" icon={<Trash2 size={16} className="text-danger-600 dark:text-danger-500" />} aria-label="Delete payment" />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Header row — page title + primary actions, above the filters. */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Typography.Title level={4} style={{ margin: 0 }}>Payments</Typography.Title>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {canRecord && (<Button variant="primary" size="sm" icon={<DollarSign size={16} />} onClick={() => setRecordOpen(true)}>Record Payment</Button>)}
        </div>
      </div>

      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}><Kpi title="Pending approval" value={inr(kpis.pendingAmt)} sub={`${kpis.pendingCnt} to review`} color="#E7B22C" icon={<Clock size={18} />} /></Col>
        <Col xs={12} md={6}><Kpi title="Approved (cash)" value={inr(kpis.approvedAmt)} sub="excl. applied credit" color="#1F8A4C" icon={<CheckCircle2 size={18} />} /></Col>
        <Col xs={12} md={6}><Kpi title="Rejected" value={String(kpis.rejectedCnt)} sub="in this view" color="#D9363E" icon={<XCircle size={18} />} /></Col>
        <Col xs={12} md={6}><Kpi title="Payments" value={String(kpis.count)} sub="shown" color="#1677FF" icon={<Wallet size={18} />} /></Col>
      </Row>

      <Card>
        <DataTable
          storageKey="payments-list"
          rowKey="id"
          loading={isLoading}
          dataSource={rows}
          columns={columns}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          toolbarLeft={
            <Space wrap>
              <Input placeholder="Payment no" style={{ width: 150 }} value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
              <Select placeholder="Status" allowClear style={{ width: 170 }} value={status} onChange={setStatus}
                options={Object.entries(APPROVAL).map(([v, m]) => ({ value: v, label: m.label }))} />
              <Select placeholder="Mode" allowClear style={{ width: 120 }} value={mode} onChange={setMode} options={MODES.map((m) => ({ value: m, label: m }))} />
              {canViewRetailers && (
                <Select placeholder="Retailer" allowClear showSearch optionFilterProp="label" style={{ width: 200 }} value={retailerId} onChange={setRetailerId}
                  options={(retailers?.data ?? []).map((r: any) => ({ value: String(r.id), label: `${r.companyName}${r.city ? ` · ${r.city}` : ''}` }))} />
              )}
              <DatePicker.RangePicker disabledDate={disableFuture} value={range} onChange={(r) => setRange(r as [Dayjs, Dayjs] | null)} />
            </Space>
          }
        />
      </Card>

      <PaymentFormModal open={recordOpen} onClose={() => setRecordOpen(false)} />
      <EditPaymentModal open={!!editFor} payment={editFor} onClose={() => setEditFor(null)} />
      <PaymentReviewModal open={!!reviewFor} payment={reviewFor} onClose={() => setReviewFor(null)} />
    </div>
  );
}

function Kpi({ title, value, sub, color, icon }: { title: string; value: React.ReactNode; sub?: string; color: string; icon: React.ReactNode }) {
  return (
    <div className="rounded-card border border-line bg-surface shadow-card" style={{ borderLeft: `3px solid ${color}` }}>
      <div style={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ color, fontSize: 18 }}>{icon}</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, color: '#888' }}>{title}</div>
            <div style={{ fontSize: 20, fontWeight: 600, color, whiteSpace: 'nowrap' }}>{value}</div>
            {sub && <div style={{ fontSize: 11, color: '#aaa' }}>{sub}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
